import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Heart, User, Plus, Edit, Trash2, X, ArrowLeft, Copy, Check } from 'lucide-react';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { useToast } from '../hooks/useToast';
import API_URL from '../config/API_URL';

const FamilyMemberCard = ({ member, onEdit, onDelete }) => {
  const roleColors = {
    admin: '#9333ea',
    reader: '#059669',
    member: '#2563eb'
  };

  return (
    <Card style={{ padding: '1rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
      <div style={{ 
        width: '60px', 
        height: '60px', 
        borderRadius: '50%', 
        background: roleColors[member.family_role] || '#6b7280', 
        color: 'white', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        fontSize: '1.5rem',
        fontWeight: 'bold'
      }}>
        {member.first_name.charAt(0)}
      </div>
      <div style={{ flex: 1 }}>
        <h4 style={{ fontWeight: '600', color: '#1f2937', marginBottom: '0.25rem' }}>{member.first_name} {member.last_name}</h4>
        <p style={{ color: '#6b7280', fontSize: '0.875rem', textTransform: 'capitalize' }}>{member.family_role}</p>
      </div>
    </Card>
  );
};

const FamilyTree = ({ members, kids }) => {
  const readers = members?.filter(m => m.family_role === 'reader') || [];
  const admins = members?.filter(m => m.family_role === 'admin') || [];

  return (
    <Card style={{ padding: '2rem', background: 'linear-gradient(135deg, #fef3c7 0%, #fce7f3 100%)' }}>
      <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '2rem', textAlign: 'center', color: '#1f2937' }}>
        Family Tree
      </h3>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2rem' }}>
        <div style={{ display: 'flex', gap: '3rem', justifyContent: 'center', flexWrap: 'wrap' }}>
          {readers.map(reader => (
            <div key={reader.id} style={{ textAlign: 'center' }}>
              <div style={{ 
                width: '80px', 
                height: '80px', 
                borderRadius: '50%', 
                background: '#db2777', 
                color: 'white', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                fontSize: '2rem',
                fontWeight: 'bold',
                margin: '0 auto 0.5rem'
              }}>
                {reader.first_name.charAt(0)}
              </div>
              <p style={{ fontWeight: '600', color: '#1f2937' }}>{reader.first_name}</p>
              <p style={{ fontSize: '0.75rem', color: '#6b7280' }}>Reader</p>
            </div>
          ))}
        </div>
        
        {readers.length > 0 && (
          <div style={{ width: '2px', height: '40px', background: '#d1d5db' }}></div>
        )}
        
        <div style={{ 
          padding: '1rem 2rem', 
          background: 'white', 
          borderRadius: '12px', 
          fontWeight: '600',
          color: '#1f2937',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          Parents
        </div>
        
        {kids?.length > 0 && (
          <div style={{ width: '2px', height: '40px', background: '#d1d5db' }}></div>
        )}
        
        <div style={{ display: 'flex', gap: '3rem', justifyContent: 'center', flexWrap: 'wrap' }}>
          {kids?.map(child => (
            <div key={child.id} style={{ textAlign: 'center' }}>
              <div style={{ 
                width: '80px', 
                height: '80px', 
                borderRadius: '50%', 
                background: '#2563eb', 
                color: 'white', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                fontSize: '2rem',
                fontWeight: 'bold',
                margin: '0 auto 0.5rem'
              }}>
                {child.first_name.charAt(0)}
              </div>
              <p style={{ fontWeight: '600', color: '#1f2937' }}>{child.first_name}</p>
              <p style={{ fontSize: '0.75rem', color: '#6b7280' }}>Age {child.age}</p>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
};

const AddKidModal = ({ isOpen, onClose, onAdd, editKid, familyId, showToast, ToastContainer }) => {
  const [formData, setFormData] = useState({ first_name: '', age: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (editKid) {
      setFormData(editKid);
    } else {
      setFormData({ first_name: '', age: '' });
    }
  }, [editKid, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const url = editKid 
        ? `${API_URL}/api/family/${familyId}/kids/${editKid.id}`
        : `${API_URL}/api/family/${familyId}/kids`;
      
      const method = editKid ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          first_name: formData.first_name,
          age: parseInt(formData.age)
        })
      });

      if (response.ok) {
        const data = await response.json();
        onAdd(data.kid);
        setFormData({ first_name: '', age: '' });
      } else {
        const error = await response.json();
        showToast(error.error || 'Failed to save kid');
      }
    } catch (error) {
      console.error('Error:', error);
      showToast('An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ 
      position: 'fixed', 
      top: 0, 
      left: 0, 
      right: 0, 
      bottom: 0, 
      background: 'rgba(0,0,0,0.5)', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem'
    }}>
      <ToastContainer />
      <Card style={{ padding: '2rem', maxWidth: '500px', width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937' }}>
            {editKid ? 'Edit Kid' : 'Add Kid'}
          </h3>
          <button 
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
          >
            <X size={24} color="#6b7280" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#1f2937' }}>
              First Name
            </label>
            <input
              type="text"
              value={formData.first_name}
              onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              required
              style={{ 
                width: '100%', 
                padding: '0.75rem', 
                borderRadius: '8px', 
                border: '1px solid #d1d5db',
                fontSize: '1rem'
              }}
              placeholder="Enter first name"
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#1f2937' }}>
              Age
            </label>
            <input
              type="number"
              value={formData.age}
              onChange={(e) => setFormData({ ...formData, age: e.target.value })}
              required
              min="0"
              max="21"
              style={{ 
                width: '100%', 
                padding: '0.75rem', 
                borderRadius: '8px', 
                border: '1px solid #d1d5db',
                fontSize: '1rem'
              }}
              placeholder="Enter age"
            />
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem' }}>
            <Button variant="secondary" onClick={onClose} style={{ flex: 1 }}>
              Cancel
            </Button>
            <Button type="submit" style={{ flex: 1 }} disabled={loading}>
              {loading ? 'Saving...' : (editKid ? 'Update' : 'Add Kid')}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

const CreateFamilyModal = ({ isOpen, onClose, onCreate, showToast, ToastContainer }) => {
  const [familyName, setFamilyName] = useState('');
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/family/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ family_name: familyName })
      });

      if (response.ok) {
        const data = await response.json();
        onCreate(data.family);
        setFamilyName('');
      } else {
        const error = await response.json();
        showToast(error.error || 'Failed to create family');
      }
    } catch (error) {
      console.error('Error:', error);
      showToast('An error occurred while creating the family');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ 
      position: 'fixed', 
      top: 0, 
      left: 0, 
      right: 0, 
      bottom: 0, 
      background: 'rgba(0,0,0,0.5)', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem'
    }}>
      <ToastContainer />
      <Card style={{ padding: '2rem', maxWidth: '500px', width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937' }}>
            Create Your Family
          </h3>
          <button 
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
          >
            <X size={24} color="#6b7280" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#1f2937' }}>
              Family Name
            </label>
            <input
              type="text"
              value={familyName}
              onChange={(e) => setFamilyName(e.target.value)}
              required
              style={{ 
                width: '100%', 
                padding: '0.75rem', 
                borderRadius: '8px', 
                border: '1px solid #d1d5db',
                fontSize: '1rem'
              }}
              placeholder="The Smith Family"
            />
          </div>

          <Button type="submit" style={{ width: '100%' }} disabled={loading}>
            {loading ? 'Creating...' : 'Create Family'}
          </Button>
        </form>
      </Card>
    </div>
  );
};

const InviteCodeDisplay = ({ familyCode }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(familyCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Card style={{ padding: '1.5rem', marginBottom: '2rem', background: 'linear-gradient(135deg, #fef3c7 0%, #fce7f3 100%)' }}>
      <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '0.5rem', color: '#1f2937' }}>
        Family Invite Code
      </h3>
      <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem' }}>
        Share this code with grandparents and caregivers so they can join your family
      </p>
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
        <div style={{ 
          background: 'white', 
          padding: '1rem 1.5rem', 
          borderRadius: '8px', 
          fontSize: '1.5rem', 
          fontWeight: 'bold', 
          letterSpacing: '0.1em',
          color: '#db2777',
          flex: 1
        }}>
          {familyCode}
        </div>
        <button
          onClick={handleCopy}
          style={{ 
            background: copied ? '#059669' : '#db2777', 
            color: 'white', 
            border: 'none', 
            borderRadius: '8px', 
            padding: '1rem',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontWeight: '600'
          }}
        >
          {copied ? <><Check size={20} /> Copied!</> : <><Copy size={20} /> Copy</>}
        </button>
      </div>
    </Card>
  );
};

export const FamilyPage = () => {
  const navigate = useNavigate();
  const [isKidModalOpen, setIsKidModalOpen] = useState(false);
  const [isCreateFamilyModalOpen, setIsCreateFamilyModalOpen] = useState(false);
  const [editingKid, setEditingKid] = useState(null);
  const [familyData, setFamilyData] = useState(null);
  const [loading, setLoading] = useState(true);

  const { showToast, ToastContainer } = useToast();
  

  useEffect(() => {
    fetchFamilyData();
  }, []);

  const fetchFamilyData = async () => {
    try {
      const familiesResponse = await fetch(`${API_URL}/api/family/my-families`, {
        credentials: 'include'
      });
      
      if (!familiesResponse.ok) {
        throw new Error('Failed to fetch families');
      }
      
      const families = await familiesResponse.json();
      
      if (families.length === 0) {
        setLoading(false);
        setIsCreateFamilyModalOpen(true);
        return;
      }
      
      const familyId = families[0].id;
      const detailResponse = await fetch(`${API_URL}/api/family/${familyId}`, {
        credentials: 'include'
      });
      
      if (!detailResponse.ok) {
        throw new Error('Failed to fetch family details');
      }
      
      const data = await detailResponse.json();
      setFamilyData(data);
    } catch (error) {
      console.error('Error fetching family data:', error);
      showToast('Failed to load family data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFamily = (family) => {
    setIsCreateFamilyModalOpen(false);
    fetchFamilyData();
  };

  const handleAddKid = (kid) => {
    if (editingKid) {
      setFamilyData(prev => ({
        ...prev,
        kids: prev.kids.map(k => k.id === kid.id ? kid : k)
      }));
      setEditingKid(null);
    } else {
      setFamilyData(prev => ({
        ...prev,
        kids: [...(prev.kids || []), kid]
      }));
    }
    setIsKidModalOpen(false);
  };

  const handleEditKid = (kid) => {
    setEditingKid(kid);
    setIsKidModalOpen(true);
  };

  const handleDeleteKid = async (kidId) => {
    if (!window.confirm('Are you sure you want to remove this kid?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/family/${familyData.id}/kids/${kidId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setFamilyData(prev => ({
          ...prev,
          kids: prev.kids.filter(k => k.id !== kidId)
        }));
      } else {
        const error = await response.json();
        showToast(error.error || 'Failed to delete kid');
      }
    } catch (error) {
      console.error('Error:', error);
      showToast('An error occurred');
    }
  };

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>;
  }

  if (!familyData) {
    return (
      <>
        <CreateFamilyModal 
          isOpen={isCreateFamilyModalOpen}
          onClose={() => navigate('/parent-dashboard')}
          onCreate={handleCreateFamily}
          showToast={showToast}
          ToastContainer={ToastContainer}
        />
      </>
    );
  }

  const canManage = familyData.my_role === 'admin';
  const readers = familyData.members?.filter(m => m.family_role === 'reader') || [];

  return (
    <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '2rem 1.5rem' }}>
      <ToastContainer />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <Button variant="secondary" onClick={() => navigate('/parent-dashboard')}>
            <ArrowLeft size={20} /> Back
          </Button>
          <h2 style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1f2937' }}>{familyData.family_name}</h2>
        </div>
        {canManage && (
          <Button onClick={() => setIsKidModalOpen(true)}>
            <Plus size={20} /> Add Kid
          </Button>
        )}
      </div>

      {canManage && <InviteCodeDisplay familyCode={familyData.family_code} />}

      <div style={{ marginBottom: '2rem' }}>
        <FamilyTree members={familyData.members} kids={familyData.kids} />
      </div>

      <div style={{ display: 'grid', gap: '2rem', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
        <div>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem', color: '#2563eb', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <User size={20} /> Children
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {!familyData.kids || familyData.kids.length === 0 ? (
              <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>No kids added yet</p>
            ) : (
              familyData.kids.map(kid => (
                <Card key={kid.id} style={{ padding: '1rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{ 
                    width: '60px', 
                    height: '60px', 
                    borderRadius: '50%', 
                    background: '#2563eb', 
                    color: 'white', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    fontSize: '1.5rem',
                    fontWeight: 'bold'
                  }}>
                    {kid.first_name.charAt(0)}
                  </div>
                  <div style={{ flex: 1 }}>
                    <h4 style={{ fontWeight: '600', color: '#1f2937', marginBottom: '0.25rem' }}>{kid.first_name}</h4>
                    <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>Age: {kid.age}</p>
                  </div>
                  {canManage && (
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button 
                        onClick={() => handleEditKid(kid)}
                        style={{ background: '#e5e7eb', border: 'none', borderRadius: '6px', padding: '0.5rem', cursor: 'pointer' }}
                      >
                        <Edit size={18} color="#6b7280" />
                      </button>
                      <button 
                        onClick={() => handleDeleteKid(kid.id)}
                        style={{ background: '#fee2e2', border: 'none', borderRadius: '6px', padding: '0.5rem', cursor: 'pointer' }}
                      >
                        <Trash2 size={18} color="#dc2626" />
                      </button>
                    </div>
                  )}
                </Card>
              ))
            )}
          </div>
        </div>

        <div>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem', color: '#db2777', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Heart size={20} /> Grandparents & Caregivers
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {readers.length === 0 ? (
              <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>No readers yet. Share your family code!</p>
            ) : (
              readers.map(member => <FamilyMemberCard key={member.id} member={member} />)
            )}
          </div>
        </div>
      </div>

      <AddKidModal 
        isOpen={isKidModalOpen} 
        onClose={() => {
          setIsKidModalOpen(false);
          setEditingKid(null);
        }}
        onAdd={handleAddKid}
        editKid={editingKid}
        familyId={familyData.id}
        showToast={showToast}
        ToastContainer={ToastContainer}
      />
    </div>
  );
};